import React, { useEffect, useState } from 'react';
import { useLocale } from '../../i18n/LocaleContext';
import { createSetupIntent, savePaymentMethod } from '../../api/billing';

// The card form on TEST DATA (no publishableKey, no Stripe): the same steps as
// the Stripe form — setup intent, confirm (with a 3-D Secure step for the
// test card that asks for it), save the payment method — against the
// in-memory test account. Nothing here leaves the browser.
//
// Test numbers (Stripe's): 4242 4242 4242 4242 succeeds, 4000 0027 6000 3184
// asks for 3-D Secure, 4000 0000 0000 0002 is declined.

export const MOCK_DECLINED = '4000000000000002';
export const MOCK_3DS = '4000002760003184';

export function luhnValid(digits) {
  if (!/^\d{12,19}$/.test(digits)) return false;
  let sum = 0;
  for (let i = 0; i < digits.length; i += 1) {
    let d = Number(digits[digits.length - 1 - i]);
    if (i % 2 === 1) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return sum % 10 === 0;
}

export function detectBrand(digits) {
  if (/^4/.test(digits)) return 'visa';
  if (/^(5[1-5]|2(2[2-9]|[3-6]\d|7[01]|720))/.test(digits)) return 'mastercard';
  if (/^3[47]/.test(digits)) return 'amex';
  if (/^6(011|5)/.test(digits)) return 'discover';
  return 'unknown';
}

/// 'MM / YY' → { month, year } or null when not a valid, unexpired date.
export function parseExpiry(value, today = new Date()) {
  const m = /^\s*(\d{1,2})\s*\/?\s*(\d{2})\s*$/.exec(value || '');
  if (!m) return null;
  const month = Number(m[1]);
  const year = 2000 + Number(m[2]);
  if (month < 1 || month > 12) return null;
  if (new Date(year, month, 1) <= new Date(today.getFullYear(), today.getMonth(), 1)) return null;
  return { month, year };
}

const groupDigits = (v) => v.replace(/\D/g, '').slice(0, 19).replace(/(\d{4})(?=\d)/g, '$1 ');

export default function MockCardForm({ brandId, onSaved, onCancel }) {
  const { t } = useLocale();
  const [number, setNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [challenge, setChallenge] = useState(null); // pending 3-D Secure
  const [secret, setSecret] = useState(null);

  useEffect(() => {
    let alive = true;
    createSetupIntent(brandId, { mock: true }).then((r) => alive && setSecret(r.clientSecret));
    return () => {
      alive = false;
    };
  }, [brandId]);

  async function save(card) {
    setBusy(true);
    try {
      await savePaymentMethod(brandId, `pm_mock_${card.last4}`, { mock: true, card });
      onSaved();
    } catch {
      setError(t('billing.card.saveFailed'));
      setBusy(false);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (busy || !secret) return;
    setError(null);
    const digits = number.replace(/\D/g, '');
    const exp = parseExpiry(expiry);
    if (!luhnValid(digits)) return setError(t('billing.mock.badNumber'));
    if (!exp) return setError(t('billing.mock.badExpiry'));
    if (!/^\d{3,4}$/.test(cvc)) return setError(t('billing.mock.badCvc'));
    if (digits === MOCK_DECLINED) return setError(t('billing.mock.declined'));
    const card = { brand: detectBrand(digits), last4: digits.slice(-4), expMonth: exp.month, expYear: exp.year };
    if (digits === MOCK_3DS) return setChallenge(card);
    return save(card);
  }

  if (challenge) {
    return (
      <div className="bill-form bill-3ds" role="dialog" aria-label={t('billing.mock.3dsTitle')} data-testid="mock-3ds">
        <strong>{t('billing.mock.3dsTitle')}</strong>
        <p className="bill-muted">{t('billing.mock.3dsBody')}</p>
        <div className="bill-form__actions">
          <button type="button" className="btn-o btn-o--dark btn-o--sm" disabled={busy} onClick={() => save(challenge)}>
            {t('billing.mock.3dsApprove')}
          </button>
          <button
            type="button"
            className="btn-o btn-o--ghost btn-o--sm"
            disabled={busy}
            onClick={() => {
              setChallenge(null);
              setError(t('billing.mock.3dsFailed'));
            }}
          >
            {t('billing.mock.3dsReject')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="bill-form" onSubmit={handleSubmit} noValidate data-testid="mock-card-form">
      <p className="bill-test-note">{t('billing.mock.note')}</p>
      <div className="bill-card-fields">
        <label className="field bill-card-fields__number">
          <span>{t('billing.mock.number')}</span>
          <input
            inputMode="numeric"
            autoComplete="off"
            placeholder="4242 4242 4242 4242"
            value={number}
            onChange={(e) => setNumber(groupDigits(e.target.value))}
            data-testid="mock-number"
          />
        </label>
        <label className="field">
          <span>{t('billing.mock.expiry')}</span>
          <input
            inputMode="numeric"
            autoComplete="off"
            placeholder={t('billing.mock.expiryPh')}
            value={expiry}
            maxLength={7}
            onChange={(e) => setExpiry(e.target.value)}
            data-testid="mock-expiry"
          />
        </label>
        <label className="field">
          <span>CVC</span>
          <input
            inputMode="numeric"
            autoComplete="off"
            placeholder="123"
            value={cvc}
            maxLength={4}
            onChange={(e) => setCvc(e.target.value.replace(/\D/g, ''))}
            data-testid="mock-cvc"
          />
        </label>
      </div>
      {error && <p className="bill-inline-error" role="alert" data-testid="card-error">{error}</p>}
      <div className="bill-form__actions">
        <button type="submit" className="btn-o btn-o--dark btn-o--sm" disabled={busy || !secret}>
          {busy ? t('billing.card.saving') : t('billing.card.save')}
        </button>
        <button type="button" className="btn-o btn-o--ghost btn-o--sm" onClick={onCancel} disabled={busy}>
          {t('billing.cancel')}
        </button>
      </div>
    </form>
  );
}
